import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import Papa from 'papaparse';
import { PositionMappingService, type CreatePositionMappingData } from '@/services/PositionMappingService';

interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

interface CsvRow {
  symbol?: string;
  tv_size?: string;
  ct_size?: string;
  tv_to_ctrader_ratio?: string;
  contract_size?: string;
  base_unit?: string;
  category?: string;
  is_active?: string;
  // User-friendly headers
  Symbol?: string;
  'TV Size'?: string;
  'CT Size'?: string;
  'Base Unit'?: string;
  Category?: string;
  'Is Active'?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  data: CreatePositionMappingData | null;
}

export function CsvImportDialog({ open, onOpenChange, onImportComplete }: CsvImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState<CsvRow[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const csvTemplate = [
    ['Symbol', 'TV Size', 'CT Size', 'Base Unit', 'Category', 'Is Active'],
    ['EURUSD', '100', '0.01', 'EUR', 'forex', 'true'],
    ['ADAUSD', '1', '10', 'ADA', 'crypto', 'true'],
    ['XAUUSD', '100', '0.01', 'XAU', 'commodity', 'true']
  ];

  const downloadTemplate = () => {
    const csv = Papa.unparse(csvTemplate);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'position_mappings_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    parseCsvFile(selectedFile);
  };

  // Normalize column headers to match expected format
  const normalizeHeaders = (data: CsvRow[]): CsvRow[] => {
    return data.map(row => {
      const normalizedRow: CsvRow = { ...row };
      
      // Map user-friendly headers to technical headers
      if (row.Symbol) normalizedRow.symbol = row.Symbol;
      if (row['TV Size']) normalizedRow.tv_size = row['TV Size'];
      if (row['CT Size']) normalizedRow.ct_size = row['CT Size'];
      if (row['Base Unit']) normalizedRow.base_unit = row['Base Unit'];
      if (row.Category) normalizedRow.category = row.Category.toLowerCase();
      if (row['Is Active']) normalizedRow.is_active = row['Is Active'].toLowerCase();
      
      // Calculate ratio: CT Size / TV Size
      if (normalizedRow.tv_size && normalizedRow.ct_size) {
        const tvSize = parseFloat(normalizedRow.tv_size);
        const ctSize = parseFloat(normalizedRow.ct_size);
        if (!isNaN(tvSize) && !isNaN(ctSize) && tvSize !== 0) {
          normalizedRow.tv_to_ctrader_ratio = (ctSize / tvSize).toString();
        }
      }
      
      // Use default contract size based on category and symbol
      if (normalizedRow.category && !normalizedRow.contract_size) {
        switch (normalizedRow.category.toLowerCase()) {
          case 'forex':
            normalizedRow.contract_size = '100000';
            break;
          case 'crypto':
            normalizedRow.contract_size = '1';
            break;
          case 'commodity':
            if (normalizedRow.symbol === 'XAUUSD') {
              normalizedRow.contract_size = '100';
            } else if (normalizedRow.symbol === 'XAGUSD') {
              normalizedRow.contract_size = '5000';
            } else {
              normalizedRow.contract_size = '1000'; // Default for other commodities
            }
            break;
          case 'index':
            normalizedRow.contract_size = '1';
            break;
          default:
            normalizedRow.contract_size = '1';
        }
      }
      
      return normalizedRow;
    });
  };

  const parseCsvFile = (file: File) => {
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          toast.error('CSV parsing errors found');
          console.error('CSV errors:', results.errors);
          return;
        }

        // Normalize headers to handle both formats
        const normalizedData = normalizeHeaders(results.data);
        setParsedData(normalizedData);
        validateData(normalizedData);
        setShowPreview(true);
      },
      error: (error) => {
        toast.error('Failed to parse CSV file');
        console.error('CSV parse error:', error);
      }
    });
  };

  const validateData = (data: CsvRow[]) => {
    const results: ValidationResult[] = data.map((row, index) => {
      const errors: string[] = [];
      
      // Validate required fields
      if (!row.symbol?.trim()) {
        errors.push('Symbol is required');
      }
      
      // Validate TV Size and CT Size for ratio calculation
      if (!row.tv_size?.trim() && !row.tv_to_ctrader_ratio?.trim()) {
        errors.push('TV Size is required');
      }
      if (!row.ct_size?.trim() && !row.tv_to_ctrader_ratio?.trim()) {
        errors.push('CT Size is required');
      }
      
      if (row.tv_size && row.ct_size) {
        const tvSize = parseFloat(row.tv_size);
        const ctSize = parseFloat(row.ct_size);
        if (isNaN(tvSize) || tvSize <= 0) {
          errors.push('TV Size must be a positive number');
        }
        if (isNaN(ctSize) || ctSize <= 0) {
          errors.push('CT Size must be a positive number');
        }
      } else if (row.tv_to_ctrader_ratio) {
        const ratio = parseFloat(row.tv_to_ctrader_ratio);
        if (isNaN(ratio) || ratio <= 0) {
          errors.push('TV to cTrader ratio must be a positive number');
        }
      }

      if (!row.contract_size?.trim()) {
        errors.push('Contract size is required');
      } else {
        const size = parseFloat(row.contract_size);
        if (isNaN(size) || size <= 0) {
          errors.push('Contract size must be a positive number');
        }
      }

      // Validate category if provided
      const validCategories = ['forex', 'crypto', 'commodity', 'stock', 'index'];
      if (row.category && !validCategories.includes(row.category.toLowerCase())) {
        errors.push(`Category must be one of: ${validCategories.join(', ')}`);
      }

      // Validate is_active if provided
      if (row.is_active && !['true', 'false', '1', '0'].includes(row.is_active.toLowerCase())) {
        errors.push('is_active must be true, false, 1, or 0');
      }

      const valid = errors.length === 0;
      let data: CreatePositionMappingData | null = null;

      if (valid) {
        // Auto-detect category from symbol if not provided
        let category = row.category?.toLowerCase() || 'forex';
        if (!row.category) {
          const symbol = row.symbol.toUpperCase();
          if (symbol.includes('BTC') || symbol.includes('ETH') || symbol.includes('USDT')) {
            category = 'crypto';
          } else if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('OIL')) {
            category = 'commodity';
          } else if (symbol.includes('SPX') || symbol.includes('NDX') || symbol.includes('DJI')) {
            category = 'index';
          }
        }

        data = {
          symbol: row.symbol.toUpperCase(),
          tv_to_ctrader_ratio: parseFloat(row.tv_to_ctrader_ratio),
          contract_size: parseFloat(row.contract_size),
          base_unit: row.base_unit?.trim() || row.symbol.substring(0, 3).toUpperCase(),
          category: category as any,
          is_active: row.is_active ? ['true', '1'].includes(row.is_active.toLowerCase()) : true
        };
      }

      return { valid, errors, data };
    });

    setValidationResults(results);
  };

  const handleImport = async () => {
    const validRows = validationResults.filter(result => result.valid && result.data);
    
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < validRows.length; i++) {
        const result = validRows[i];
        if (!result.data) continue;

        try {
          await PositionMappingService.createMapping(result.data);
          successCount++;
        } catch (error) {
          console.error(`Failed to import ${result.data.symbol}:`, error);
          errorCount++;
        }

        setImportProgress(((i + 1) / validRows.length) * 100);
      }

      if (successCount > 0) {
        // Clear cache to refresh mappings
        PositionMappingService.clearCache();
        toast.success(`Successfully imported ${successCount} position mappings`);
        if (errorCount > 0) {
          toast.warning(`${errorCount} rows failed to import`);
        }
        onImportComplete();
        onOpenChange(false);
        resetState();
      } else {
        toast.error('No rows were imported successfully');
      }
    } catch (error) {
      toast.error('Import failed');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const resetState = () => {
    setFile(null);
    setParsedData([]);
    setValidationResults([]);
    setShowPreview(false);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.length - validCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Position Mappings from CSV</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Template Download */}
          <Alert>
            <Download className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Download the CSV template to ensure proper formatting</span>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </AlertDescription>
          </Alert>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="csv-file">Select CSV File</Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={importing}
              />
              {file && (
                <Button variant="outline" size="sm" onClick={resetState}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Import Progress */}
          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Importing mappings...</span>
                <span>{Math.round(importProgress)}%</span>
              </div>
              <Progress value={importProgress} />
            </div>
          )}

          {/* Preview and Validation */}
          {showPreview && !importing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Import Preview</h3>
                <div className="flex items-center gap-4">
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Valid: {validCount}
                  </Badge>
                  {invalidCount > 0 && (
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Invalid: {invalidCount}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-auto">
                <div className="space-y-2 p-4">
                  {validationResults.map((result, index) => (
                    <div
                      key={index}
                      className={`border rounded p-3 ${
                        result.valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {result.valid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className="font-medium">
                            {result.data?.symbol || parsedData[index]?.symbol || `Row ${index + 1}`}
                          </span>
                        </div>
                        {result.data && (
                          <div className="text-sm text-muted-foreground">
                            {result.data.category} • Ratio: {result.data.tv_to_ctrader_ratio}
                          </div>
                        )}
                      </div>
                      {result.errors.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          <ul className="list-disc list-inside">
                            {result.errors.map((error, i) => (
                              <li key={i}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          {showPreview && validCount > 0 && (
            <Button onClick={handleImport} disabled={importing}>
              <Upload className="h-4 w-4 mr-2" />
              Import {validCount} Mappings
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}